import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";
import { useUserProfile } from "@/api/Users";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { machineFormat } from "@medoma/models/date-and-time";
import {
  organizationSchema,
  type IOrganization,
} from "@medoma/models/organizations";
import {
  addDays,
  addHours,
  addMinutes,
  endOfToday,
  startOfDay,
  startOfToday,
} from "date-fns";
import { useMemo } from "react";

export const NO_TIMESPAN = "";

// widen as required
type SelectableTimespan =
  | {
      id: "dayTime1";
      timespan: "07:00-13:00";
      displayValue: MessageDescriptor;
    }
  | {
      id: "dayTime2";
      timespan: "07:00-16:00";
      displayValue: MessageDescriptor;
    }
  | {
      id: "dayTime3";
      timespan: "07:15-12:30";
      displayValue: MessageDescriptor;
    }
  | {
      id: "dayTime4";
      timespan: "07:00-12:45";
      displayValue: MessageDescriptor;
    }
  | {
      id: "dayTime5";
      timespan: "07:00-11:30";
      displayValue: MessageDescriptor;
    }
  | {
      id: "eveningTime1";
      timespan: "12:00-20:45";
      displayValue: MessageDescriptor;
    }
  | {
      id: "eveningTime2";
      timespan: "16:00-20:45";
      displayValue: MessageDescriptor;
    }
  | {
      id: "eveningTime3";
      timespan: "09:30-22:00";
      displayValue: MessageDescriptor;
    }
  | {
      id: "eveningTime4";
      timespan: "13:15-20:45";
      displayValue: MessageDescriptor;
    }
  | {
      id: "eveningTime5";
      timespan: "11:30-21:00";
      displayValue: MessageDescriptor;
    }
  | {
      id: "nightTime1";
      timespan: "20:45-07:00";
      displayValue: MessageDescriptor;
    }
  | {
      id: "nightTime2";
      timespan: "20:50-07:00";
      displayValue: MessageDescriptor;
    }
  | {
      id: "nightTime3";
      timespan: "21:00-06:45";
      displayValue: MessageDescriptor;
    };

const SELECTABLE_TIMESPANS_PER_ORGANIZATION: Record<
  IOrganization,
  SelectableTimespan[]
> = {
  [organizationSchema.enum.capioahc]: [
    {
      id: "dayTime1",
      timespan: "07:00-13:00",
      displayValue: msg`Dag (07-13)`,
    },
    {
      id: "dayTime2",
      timespan: "07:00-16:00",
      displayValue: msg`Dag (07-16)`,
    },
    {
      id: "eveningTime1",
      timespan: "12:00-20:45",
      displayValue: msg`Kväll (12-20.45)`,
    },
    {
      id: "eveningTime2",
      timespan: "16:00-20:45",
      displayValue: msg`Kväll (16-20.45)`,
    },
    {
      id: "nightTime1",
      timespan: "20:45-07:00",
      displayValue: msg`Natt (20.45-07)`,
    },
  ],
  [organizationSchema.enum.medoma]: [
    {
      id: "dayTime2",
      timespan: "07:00-16:00",
      displayValue: msg`Dag (07-16)`,
    },
    {
      id: "eveningTime1",
      timespan: "12:00-20:45",
      displayValue: msg`Kväll (12-20.45)`,
    },
    {
      id: "nightTime1",
      timespan: "20:45-07:00",
      displayValue: msg`Natt (20.45-07)`,
    },
  ],
  [organizationSchema.enum.qa]: [
    {
      id: "dayTime2",
      timespan: "07:00-16:00",
      displayValue: msg`Dag (07-16)`,
    },
    {
      id: "eveningTime1",
      timespan: "12:00-20:45",
      displayValue: msg`Kväll (12-20.45)`,
    },
    {
      id: "nightTime1",
      timespan: "20:45-07:00",
      displayValue: msg`Natt (20.45-07)`,
    },
  ],
  [organizationSchema.enum.hahag]: [],
  [organizationSchema.enum.ksgr]: [],
  [organizationSchema.enum.rakus]: [],
  [organizationSchema.enum.slso]: [
    {
      id: "dayTime5",
      timespan: "07:00-11:30",
      displayValue: msg`Dag (07-11.30)`,
    },
    {
      id: "eveningTime5",
      timespan: "11:30-21:00",
      displayValue: msg`Kväll (11.30-21.00)`,
    },
    {
      id: "nightTime3",
      timespan: "21:00-06:45",
      displayValue: msg`Natt (21-06.45)`,
    },
  ],
  [organizationSchema.enum.ssh]: [
    {
      id: "dayTime4",
      timespan: "07:00-12:45",
      displayValue: msg`Dag (07-12.45)`,
    },
    {
      id: "eveningTime4",
      timespan: "13:15-20:45",
      displayValue: msg`Kväll (13.15-20.45)`,
    },
    {
      id: "nightTime2",
      timespan: "20:50-07:00",
      displayValue: msg`Natt (20.50-07)`,
    },
  ],
  [organizationSchema.enum.sus]: [
    {
      id: "dayTime3",
      timespan: "07:15-12:30",
      displayValue: msg`Dag (07.15-12.30)`,
    },
    {
      id: "eveningTime3",
      timespan: "09:30-22:00",
      displayValue: msg`Kväll (09.30-22)`,
    },
  ],
};

export const selectableTimeSpans = (organization: IOrganization) =>
  [
    {
      id: "fullDay",
      timespan: NO_TIMESPAN,
      displayValue: msg`Hela dygnet`,
    },
    ...SELECTABLE_TIMESPANS_PER_ORGANIZATION[organization],
  ] as const;

export const useManageTimeFilter = () => {
  const { userId } = useUserProfile();
  return useLocalStorage({
    key: "planning.timeFilter",
    userId,
    initialValue: NO_TIMESPAN,
  });
};

export const useTimeFilter = () => {
  // A `Date` object is not a good dependency, since it changes all the time. Convert to `'Wed Apr 09 2025'` format and back.
  const selectedDateString = useSelectedDate().toDateString();
  const [timeFilter] = useManageTimeFilter();

  return useMemo(() => {
    if (timeFilter === NO_TIMESPAN) {
      return null;
    }

    const startOfSelectedDate = startOfDay(new Date(selectedDateString));
    const [startTimeString, endTimeString] = timeFilter.split("-");

    // We don't really handle time zones or summer/winter time. Should not be a concern.
    const [startHours, startMinutes] = (
      startTimeString ?? machineFormat(startOfToday(), "HH:mm")
    )
      .split(":")
      .map((part) => parseInt(part));

    const [endHours, endMinutes] = (
      endTimeString ?? machineFormat(endOfToday(), "HH:mm")
    )
      .split(":")
      .map((part) => parseInt(part));

    // In case of any missing data, fall back to show as much data as possible
    const startTime = addMinutes(
      addHours(startOfSelectedDate, startHours ?? 0),
      startMinutes ?? 0,
    );
    const endTime = addMinutes(
      addHours(startOfSelectedDate, endHours ?? 23),
      endMinutes ?? 59,
    );

    // Handle scenario where endTime is less than startTime, typical for night shifts
    if (endTime < startTime) {
      return { startTime, endTime: addDays(endTime, 1) };
    }

    return { startTime, endTime };
  }, [timeFilter, selectedDateString]);
};
